<template>
  <v-row no-gutters>
    <v-col cols="12" class="mb-4">
      <h3 class="header__title--main">
        {{ $t('message.pages.analytics.browsers') }}
      </h3>
    </v-col>
    <v-card v-if="noInfoFound" width="1000px" height="300px" flat>
      <empty-state />
    </v-card>
    <v-row no-gutters justify="center" v-else>
      <v-card
        flat
        v-if="loading"
        class="d-flex align-center justify-center px-3"
        height="273px"
        min-width="866px"
      >
        <loading-logo :value="200" />
      </v-card>
      <v-col cols="12" v-else>
        <v-row
          v-if="errors.fetchError"
          class="flex-column"
          justify="center"
          align="center"
          no-gutters
          style="min-height: 250px"
        >
          <v-card flat height="30" class="mb-4">
            <span class="footerText align-text d-block">
              {{ errors.shownErrMsg }}
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon size="18" v-bind="attrs" v-on="on">
                    mdi-help-circle
                  </v-icon>
                </template>
                <v-container class="text-body-2 pa-0" style="max-width: 230px">
                  {{ errors.tooltipErrMsg }}
                </v-container>
              </v-tooltip>
            </span>
          </v-card>
          <v-btn color="primary" class="ml-3" @click="fetchData">{{
            $t('message.pages.analytics.tryAgain')
          }}</v-btn>
        </v-row>
        <v-row v-else no-gutters align="center">
          <v-card width="450px" flat>
            <span>{{ $t('message.pages.analytics.byViews') }}</span>
          </v-card>
          <v-col cols="12" class="mt-5">
            <v-row no-gutters align="start">
              <v-card flat width="285px">
                <vue-apex-charts
                  style="position: relative; right: 30px"
                  width="300"
                  type="donut"
                  :options="options"
                  :series="series"
                ></vue-apex-charts>
              </v-card>
              <v-col class="mt-3">
                <v-row no-gutters>
                  <v-col
                    cols="4"
                    v-for="(device, i) in allBrowsersData"
                    :key="i"
                  >
                    <v-card width="100%" height="75px" flat>
                      <v-row no-gutters>
                        <v-col>
                          <v-row no-gutters>
                            <v-col cols="12" class="d-flex">
                              <v-icon class="mr-3 pb-1">mdi-web</v-icon>
                              <span
                                class="devicesText"
                                :style="`color: ${device.color};`"
                              >
                                {{ Number(device.view).toLocaleString() }}
                                <span class="devicesPercentageText pl-1">
                                  {{ device.percentage }}%
                                </span>
                              </span>
                            </v-col>
                            <v-col cols="12">
                              <span
                                class="text-caption ml-9"
                                style="color: #757575"
                              >
                                {{ device.name }}
                              </span>
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-if="loading" cols="12" class="mt-14">
        <v-skeleton-loader type="table-tbody" />
      </v-col>
      <template v-else-if="!errors.fetchError">
        <v-col cols="12" class="mt-14 px-4">
          <v-card height="48px" width="100%" flat class="d-flex align-center">
            <v-row
              no-gutters
              style="
                font-size: 12px !important;
                font-weight: bold;
                color: var(--v-blueMarine-base);
              "
            >
              <v-col>
                <span>{{ $t('message.pages.analytics.browsers') }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ $t('message.pages.analytics.account.views') }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{
                  $t('message.pages.analytics.account.uniqueViews')
                }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                <span>{{ $t('message.pages.analytics.account.plays') }}</span>
              </v-col>
              <v-col class="d-flex justify-end pr-2">
                <span>{{
                  $t('message.pages.analytics.account.uniquePlays')
                }}</span>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col
          cols="12"
          class="px-4"
          style="overflow-y: auto"
          :style="`height: ${tableHeight}`"
        >
          <v-card
            v-for="(deviceData, i) in allBrowsersData"
            height="48px"
            width="100%"
            flat
            style="border-top: 1px solid #e6e8f8; border-radius: 0px"
            class="d-flex align-center"
            :key="i"
          >
            <v-row no-gutters class="body-2">
              <v-col>
                <span>{{ deviceData.name }}</span>
              </v-col>
              <v-col class="d-flex justify-end">
                {{ Number(deviceData.view).toLocaleString() }}
              </v-col>
              <v-col class="d-flex justify-end">
                {{ Number(deviceData.unique_view).toLocaleString() }}
              </v-col>
              <v-col class="d-flex justify-end">
                {{ Number(deviceData.play).toLocaleString() }}
              </v-col>
              <v-col class="d-flex justify-end pr-2">
                {{ Number(deviceData.unique_play).toLocaleString() }}
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </template>
    </v-row>
  </v-row>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import { format } from 'date-fns'
import LoadingLogo from '@/components/LoadingLogo.vue'
import EmptyState from './EmptyState.vue'
import i18n from '@/translations/lang'

export default {
  name: 'Browsers',
  components: {
    VueApexCharts,
    LoadingLogo,
    EmptyState,
  },
  props: {
    componentData: {
      type: Object,
      default: () => ({ selectedVideos: [], dateRange: [] }),
      required: true,
    },
    hasAnyVideoInFilter: {
      type: Boolean,
      default: false,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    browsersViews: [],
    options: {
      colors: ['#9C27B0', '#FF9800', '#E91E63'],
      tooltip: {
        enabled: false,
      },
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      donut: {
        labels: {
          show: false,
        },
        total: {
          show: false,
          showAlways: false,
        },
      },
    },
    series: [],
    allBrowsersData: [],
    noInfoFound: false,
    colors: [],
    errors: {
      fetchError: false,
      shownErrMsg: '',
      tooltipErrMsg: '',
    },
  }),
  computed: {
    tableHeight() {
      const length = this.allBrowsersData.length
      if (length < 5) {
        return 267
      } else {
        return length * 48 + 5
      }
    },
    selectedVideos() {
      const { selectedVideos } = this.componentData
      return selectedVideos
    },
    dateRange() {
      const { dateRange } = this.componentData
      return dateRange
    },
  },
  watch: {
    componentData: {
      handler() {
        this.fetchData()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    resetData() {
      this.allBrowsersData = []
      this.browsersViews = []
      this.series = []
      this.colors = ['#4A148C', '#10859E', '#00695C', '#9E9D24', '#EF6C00']
      this.noInfoFound = false
    },
    async fetchData() {
      this.clearErrors()
      this.resetData()
      this.loading = true
      await this.getDevicesInfo()
      this.loading = false
    },
    async getSelectedVideosBrowserInfo() {
      let devicesResponses = []
      let devicesData = {}
      if (this.hasAnyVideoInFilter) return (this.noInfoFound = true)
      for (let video of this.selectedVideos) {
        let ret = await newAnalyticsApi.getSingleBrowsersMetrics(
          video.id,
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        devicesResponses.push(ret.data)
      }
      devicesResponses.forEach((device) => {
        let eachDeviceKeys = Object.keys(device)
        eachDeviceKeys.forEach((key) => {
          if (key == undefined || key === 'undefined') return
          let deviceKey = devicesData[`${key}`]
          if (!deviceKey) devicesData[`${key}`] = {}
          let browserInfo = device[key]
          let eachBrowserDateArray = Object.keys(browserInfo)
          eachBrowserDateArray.forEach((day) => {
            let checkIfObjectHasDay = devicesData[key][day]
            if (!checkIfObjectHasDay)
              devicesData[key][day] = {
                play: 0,
                view: 0,
                unique_play: 0,
                unique_view: 0,
              }
            devicesData[key][day]['play'] += device[key][day]['play'] ?? 0
            devicesData[key][day]['unique_play'] +=
              device[key][day]['unique_play'] ?? 0
            devicesData[key][day]['unique_view'] +=
              device[key][day]['unique_view'] ?? 0
            devicesData[key][day]['view'] += device[key][day]['view'] ?? 0
          })
        })
      })
      this.setBrowsersData(devicesData)
    },
    async getDevicesInfo() {
      if (this.selectedVideos.length) {
        await this.getSelectedVideosBrowserInfo()
        return
      }
      try {
        const ret = await newAnalyticsApi.getBrowsersMetrics(
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        if (!Object.keys(ret.data).length) {
          this.noInfoFound = true
          return
        }
        this.setBrowsersData(ret.data)
      } catch (e) {
        this.handleFetchError(e)
      }
    },
    setBrowsersData(data) {
      let devicesData = data
      let allBrowsersDataAggregated = {}
      let sumOfBrowsers = 0

      this.resetData()

      for (let deviceData in devicesData) {
        let deviceObject = {
          view: 0,
          play: 0,
          unique_view: 0,
          unique_play: 0,
        }
        for (let day in devicesData[`${deviceData}`]) {
          let eachDay = devicesData[`${deviceData}`][`${day}`]
          deviceObject.view += eachDay.view ?? 0
          deviceObject.play += eachDay.play ?? 0
          deviceObject.unique_view += eachDay.unique_view ?? 0
          deviceObject.unique_play += eachDay.unique_play ?? 0
        }
        sumOfBrowsers += deviceObject.view ?? 0
        allBrowsersDataAggregated[`${deviceData}`] = deviceObject
      }
      let graphColors = []
      let percentages = []
      let sortedData = Object.entries(allBrowsersDataAggregated)
        .map((item) => {
          return {
            name: item[0],
            ...item[1],
          }
        })
        .sort((a, b) => b.view - a.view)
      let othersObject = {
        name: 'Others',
        play: 0,
        unique_play: 0,
        unique_view: 0,
        view: 0,
        color: '#3e50f7',
        percentage: 0,
      }
      for (let i = 0; i < sortedData.length; i++) {
        const maxShown = 8
        if (i < maxShown) {
          let percentage = Number(
            ((100 * sortedData[i].view) / sumOfBrowsers).toFixed(2)
          )
          percentages.push(percentage)
          let randomColor = this.colors[0] ?? '#3e50f7'
          graphColors.push(randomColor)
          this.colors.shift()
          this.allBrowsersData.push({
            color: randomColor,
            percentage: percentage,
            ...sortedData[i],
          })
        } else {
          othersObject.play += sortedData[i].play ?? 0
          othersObject.unique_play += sortedData[i].unique_play ?? 0
          othersObject.unique_view += sortedData[i].unique_view ?? 0
          othersObject.view += sortedData[i].view ?? 0
        }
      }
      if (othersObject.view > 0) {
        othersObject.percentage = Number(
          ((100 * othersObject.view) / sumOfBrowsers).toFixed(2)
        )
        percentages.push(othersObject.percentage)
        graphColors.push(othersObject.color)
        this.allBrowsersData.push(othersObject)
      }
      this.options.colors = graphColors
      this.series = percentages
    },
    handleFetchError(err) {
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err.msg ??
        err.response.data.errMsg ??
        err.response.data.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },
  },
}
</script>
